import React, { useState } from "react";

const ImageUploader = ({
  value,
  onChange,
  label = "Загрузить изображение",
  hint = "",
  disabled = false,
  buttonText = "Выбрать изображение",
  onUpload,
  showUrlInput = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [urlInput, setUrlInput] = useState("");

  // Функция для нормализации URL
  const normalizeImageUrl = (url) => {
    if (!url) return '';
    
    // Если URL уже полный (начинается с http:// или https://)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Если это относительный путь, начинающийся с /
    if (url.startsWith('/')) {
      // Проверяем, не является ли это уже путем от ut-ka.ru
      if (url.startsWith('/images/')) {
        return `https://ut-ka.ru${url}`;
      }
      // Для других относительных путей тоже добавляем домен
      return `https://ut-ka.ru${url}`;
    }
    
    // Если это просто имя файла, добавляем полный путь
    return `https://ut-ka.ru/images/${url}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Проверяем тип файла
    if (!file.type.startsWith('image/')) {
      setError('Пожалуйста, выберите файл изображения (JPG, PNG, WebP)');
      return;
    }

    // Проверяем размер файла (максимум 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Размер файла не должен превышать 10MB');
      return;
    }

    setUploading(true);
    setError("");

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch("https://ut-ka.ru/api/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      
      // Логируем ответ для отладки
      console.log("API Response:", data);

      if (!res.ok) {
        throw new Error(data.error || data.message || `Ошибка загрузки: ${res.status}`);
      }

      // Проверяем различные варианты ответа от сервера
      let imageUrl = '';
      
      // Если API возвращает полный URL
      if (data.url && (data.url.startsWith('http://') || data.url.startsWith('https://'))) {
        imageUrl = data.url;
      } 
      // Если API возвращает относительный путь или имя файла
      else if (data.filename || data.url || data.imageUrl) {
        const rawUrl = data.filename || data.url || data.imageUrl;
        imageUrl = normalizeImageUrl(rawUrl);
      } 
      // Если API возвращает другой формат (например, {file: "filename.jpg"})
      else if (data.file) {
        imageUrl = normalizeImageUrl(data.file);
      }
      else {
        // Для отладки показываем полный ответ
        throw new Error(`Сервер вернул неожиданный формат. Ответ: ${JSON.stringify(data)}`);
      }

      console.log("Normalized image URL:", imageUrl);

      // Если есть onUpload - вызываем его, иначе используем onChange
      if (onUpload) {
        onUpload(imageUrl);
      } else {
        onChange(imageUrl);
      }
      
      // Очищаем input file
      e.target.value = '';
      
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Ошибка при загрузке изображения");
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = (e) => {
    e?.preventDefault(); // Безопасно для случая, когда нет события
    if (!urlInput.trim()) {
      setError("Введите URL изображения");
      return;
    }

    try {
      // Проверяем, что это валидный URL
      new URL(urlInput);
      
      // Нормализуем URL если он относительный
      const normalizedUrl = normalizeImageUrl(urlInput.trim());
      
      if (onUpload) {
        onUpload(normalizedUrl);
      } else {
        onChange(normalizedUrl);
      }
      
      setUrlInput("");
      setError("");
    } catch (err) {
      // Если не валидный URL, может быть относительный путь
      if (urlInput.trim().startsWith('/')) {
        const normalizedUrl = normalizeImageUrl(urlInput.trim());
        if (onUpload) {
          onUpload(normalizedUrl);
        } else {
          onChange(normalizedUrl);
        }
        setUrlInput("");
        setError("");
      } else {
        setError("Пожалуйста, введите корректный URL (например, https://example.com/image.jpg или /images/file.jpg)");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const handleRemoveImage = () => {
    if (onUpload) {
      onUpload("");
    } else {
      onChange("");
    }
  };

  // Нормализуем текущее значение для отображения
  const displayValue = value ? normalizeImageUrl(value) : value;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>

      {displayValue && (
        <div className="mb-4">
          <div className="relative inline-block">
            <img
              src={displayValue}
              alt="Preview"
              className="w-40 h-40 object-cover rounded-lg border-2 border-gray-600"
              onError={(e) => {
                console.error("Image failed to load:", displayValue);
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiMzMzMiLz48cGF0aCBkPSJNNjAgNDBMNDAgODBINDBMODAgNDBIMTAwTDEwMCA4MEg4MEw2MCA0MFoiIGZpbGw9IiM2NjYiLz48L3N2Zz4=';
                e.target.alt = 'Ошибка загрузки изображения';
              }}
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              title="Удалить изображение"
            >
              ×
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1 break-all">
            URL: {displayValue}
          </p>
        </div>
      )}

      {/* Загрузка файла */}
      <div className="mb-3">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id={`uploader-file-${label.replace(/\s+/g, '-')}`}
          disabled={uploading || disabled}
        />
        
        <label
          htmlFor={`uploader-file-${label.replace(/\s+/g, '-')}`}
          className={`px-4 py-2 rounded-lg cursor-pointer text-sm inline-flex items-center ${
            disabled 
              ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-blue-600 to-purple-500 hover:opacity-90 text-white'
          }`}
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Загрузка...
            </>
          ) : (
            buttonText
          )}
        </label>
      </div>

      {/* Ввод URL (опционально) - БЕЗ формы */}
      {showUrlInput && (
        <div className="mb-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="https://ut-ka.ru/images/filename.jpg или /images/filename.jpg"
              className="flex-1 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleUrlSubmit}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              disabled={disabled || !urlInput.trim()}
            >
              Добавить
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Или введите URL/путь изображения
          </p>
        </div>
      )}

      {/* Сообщения */}
      {hint && (
        <p className="text-xs text-gray-400 mt-1">
          {hint}
        </p>
      )}

      {error && (
        <div className="mt-2 p-2 bg-red-900/20 border border-red-800 rounded-lg">
          <p className="text-xs text-red-300 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        </div>
      )}

      {/* Информация об API */}
      <div className="mt-2 text-xs text-gray-500 space-y-1">
        <p>• Изображения загружаются на сервер ut-ka.ru</p>
        <p>• Поддерживаются JPG, PNG, WebP, AVIF (макс. 10MB)</p>
        <p>• API: https://ut-ka.ru/api/upload</p>
        <p>• Все URL автоматически преобразуются в полные</p>
      </div>
    </div>
  );
};

export default ImageUploader;